import i18n from '@/i18n';
import { NotificationType } from '@nxtpeople/nxt-styles';
import * as Sentry from '@sentry/vue';
import axios, { AxiosError, AxiosResponse } from 'axios';
import type { CustomAxiosRequestConfig } from '../helpers/axiosHelper';
import store from '../store';

interface ErrorData {
   errors?: Record<string, any>;
   errorMessage?: string;
   message?: string;
}

let errorQueue: string[] = [];
let errorTimeout: NodeJS.Timeout | null = null;

// Supports flat validation errors ({ field: [message] }) and errors grouped per entity,
// e.g. { 'Isabella Jones': { candidate_id: ['NxtPlan: is already in this pool.'] } }.
function flattenErrorMessages(errors: unknown): string[] {
   if (errors === null || errors === undefined) return [];
   if (Array.isArray(errors)) return errors.flatMap(flattenErrorMessages);
   if (typeof errors === 'object') {
      return Object.entries(errors as Record<string, unknown>).flatMap(([key, value]) => {
         const messages = flattenErrorMessages(value);
         const isGroupedByEntity = typeof value === 'object' && value !== null && !Array.isArray(value);
         return isGroupedByEntity ? messages.map((message) => `${key}: ${message}`) : messages;
      });
   }
   return [String(errors)];
}

function getErrorMessage(error: AxiosError): string | null {
   try {
      const errorData = error?.response?.data as ErrorData;

      if (errorData?.errors) {
         const errorMessages = flattenErrorMessages(errorData.errors);
         if (errorMessages.length) return errorMessages.join('\r\n');
      }
      if (errorData?.errorMessage) return errorData.errorMessage;
      if (errorData?.message) return errorData.message;

      if ([400, 422].includes(error.status as number)) {
         return i18n.global.t('feedback.validation');
      }

      if (error.status === 403) {
         return i18n.global.t('feedback.not-allowed');
      }

      if (error.status === 404) {
         return i18n.global.t('feedback.not-found');
      }

      return null;
   } catch {
      return null;
   }
}

function captureSentryError(error: AxiosError): void {
   try {
      const errorName = `[${error.status} ${error.code}] - ${error.config?.url}`;
      Sentry.captureException(new Error(errorName), {
         extra: {
            stack: error.stack,
            request: {
               url: error.config?.url,
               method: error.config?.method,
               headers: error.config?.headers,
               params: error.config?.params,
               data: error.config?.data,
            },
            response: {
               code: error.code,
               message: error.message,
               status: error.status,
               data: error.response?.data,
            },
         },
      });
   } catch {
      console.warn('Error while capturing error');
   }
}

function notifyErrors() {
   if (errorQueue.length > 0) {
      const errorMessage = errorQueue.join('\r\n');
      store.dispatch('addNotification', {
         notification: { type: NotificationType.Danger, title: errorMessage || i18n.global.t('feedback.error') },
      });
      errorQueue = [];
   }
}

const errorResponseSuccessInterceptor = function (response: AxiosResponse<any>) {
   return response;
};

const errorResponseErrorInterceptor = function (error: AxiosError<ErrorData>) {
   if ([400, 404, 500].includes(error.status as number)) captureSentryError(error);
   const isCancelException = axios.isCancel(error);
   const isAbortedException = error.message === 'Request aborted';

   if (isCancelException || isAbortedException) return Promise.reject('Request aborted');

   if ((error.config as CustomAxiosRequestConfig)?.skipErrorNotification) {
      return Promise.reject(error);
   }

   let errorMessage = getErrorMessage(error);

   if (!errorMessage) {
      errorMessage = `${i18n.global.t('feedback.error')}\n${(error as any).message ?? ''}`;
   }

   if (!errorQueue.includes(errorMessage)) {
      errorQueue.push(errorMessage);
   }

   if (!errorTimeout) {
      errorTimeout = setTimeout(() => {
         notifyErrors();
         errorTimeout = null;
      }, 300);
   }

   return Promise.reject(error);
};

export { errorResponseErrorInterceptor, errorResponseSuccessInterceptor };
